import { SkipBackwardIcon } from '@smart-tv/components/icons/skip-backward';
import { SkipForwardIcon } from '@smart-tv/components/icons/skip-forward';
import React, { useCallback, useRef, useState } from 'react';
import { IconButton } from '@smart-tv/components/icon-button/icon-button';
import {
  PLAYER_FOCUS_KEY_SKIP_BACKWARD,
  PLAYER_FOCUS_KEY_SKIP_FORWARD
} from '@smart-tv/components/player/player-helpers';
import { usePlayerContext } from '@smart-tv/components/player/_contexts/player-context';
import useStream from '../../_hooks/use-stream';
import { StreamType } from '@smart-tv/typings/content';
import { IconTypes, useToast } from '@smart-tv/hooks/use-toast/use-toast';

import { useSetAtom, atom, useAtomValue } from 'jotai';
import { playerBtnsFocusHistoryAtom } from './use-player-controls-navigation';
import disableFastForwardCatchup from '../utils/disable-fastforward-catchup';
import { autoHideTimeoutExtensionAtom } from '../_hooks/use-player-controls';
import { playerWaitingAtom } from '../../atoms';
import useDeactivatePlayerActions from '../_hooks/use-deactivate-player-actions';
import testIds from '@smart-tv/e2e/shared/variables';

const SKIP_BACKWARD_AMOUNT = 10;
const SKIP_FORWARD_AMOUNT = 30;

export const isSkippingAtom = atom(false);
export const isSkipButtonInProgressAtom = atom(false);
export const validatedTargetPositionAtom = atom<number>(0);
export const startTimeAtom = atom<number | null>(null);

const useSkipButtons = () => {
  const { player, setIsPlaying } = usePlayerContext();
  const {
    streamType,
    fastForwardLimitSeconds,
    stream: { duration: durationSeconds = 0, trickmodes = 0 } = {}
  } = useStream();
  const { showToast } = useToast();
  const setIsSkipping = useSetAtom(isSkippingAtom);
  const setIsSkipButtonInProgress = useSetAtom(isSkipButtonInProgressAtom);
  const setValidatedTargetPosition = useSetAtom(validatedTargetPositionAtom);
  const setPlayerBtnsFocusHistory = useSetAtom(playerBtnsFocusHistoryAtom);
  const setAutoHideTimeoutExtension = useSetAtom(autoHideTimeoutExtensionAtom);
  const playerWaiting = useAtomValue(playerWaitingAtom);

  const [pseudoPlayerPosition, setPseudoPlayerPosition] = useState<number>(-1);

  const timeoutSkipIdRef = useRef<NodeJS.Timeout>();

  const [currentFFSeekSpeedMs, setCurrentFFSeekSpeedMs] = useState<number>(0);
  const [currentRWSeekSpeedMs, setCurrentRWSeekSpeedMs] = useState<number>(0);
  const [startTimeFF, setStartTimeFF] = useState<number | null>(null);
  const [startTimeRW, setStartTimeRW] = useState<number | null>(null);

  const idealSeekSpeed = useCallback((duration: number): number => {
    return duration / 500;
  }, []);

  const skip = useCallback(
    (skipForward: boolean) => {
      if (!player || playerWaiting) return;

      const playerPosition =
        pseudoPlayerPosition === -1 ? player.getPosition() : pseudoPlayerPosition;

      if (
        skipForward &&
        (streamType === StreamType.CatchUp || streamType === StreamType.Vod) &&
        disableFastForwardCatchup(trickmodes) &&
        fastForwardLimitSeconds - playerPosition < 1
      ) {
        showToast('Af rettighedsmæssige årsager kan der ikke spoles længere frem, end hvor du er nået til.', IconTypes.info);
        return;
      }

      player.pause();
      setIsPlaying(false);
      setIsSkipping(true);
      setIsSkipButtonInProgress(true);
      setAutoHideTimeoutExtension(3000);

      const duration = streamType === StreamType.StartOver ? durationSeconds : player.getDuration();

      if (startTimeFF && skipForward) {
        setCurrentRWSeekSpeedMs(0);
        if (Date.now() - (startTimeFF ?? Date.now()) < 100) {
          setCurrentFFSeekSpeedMs((prev) => {
            const newSpeed = prev + idealSeekSpeed(duration) / 80;
            return newSpeed > idealSeekSpeed(duration) ? prev + idealSeekSpeed(duration) : newSpeed;
          });
        } else {
          setCurrentFFSeekSpeedMs((prev) => prev + idealSeekSpeed(duration));
        }
      }

      if (startTimeRW && !skipForward) {
        setCurrentFFSeekSpeedMs(0);
        if (Date.now() - (startTimeRW ?? Date.now()) < 100) {
          setCurrentRWSeekSpeedMs((prev) => {
            const newSpeed = prev + idealSeekSpeed(duration) / 80;
            return newSpeed > idealSeekSpeed(duration) ? prev + idealSeekSpeed(duration) : newSpeed;
          });
        } else {
          setCurrentRWSeekSpeedMs((prev) => prev + idealSeekSpeed(duration));
        }
      }

      if (startTimeFF === null && skipForward) {
        setCurrentRWSeekSpeedMs(SKIP_BACKWARD_AMOUNT);

        setStartTimeFF(Date.now());
      }

      if (startTimeRW === null && !skipForward) {
        setCurrentRWSeekSpeedMs(SKIP_BACKWARD_AMOUNT);

        setStartTimeRW(Date.now());
      }

      // Extend the timeout if the user presses the skip button again before the timeout is done
      clearTimeout(timeoutSkipIdRef.current);

      const seekableRange = player.getSeekableRange();

      const skipForwardAmount = startTimeFF ? currentFFSeekSpeedMs : SKIP_FORWARD_AMOUNT;
      const skipBackwardAmount = startTimeRW ? currentRWSeekSpeedMs : SKIP_BACKWARD_AMOUNT;

      const desiredTargetPosition = skipForward
        ? playerPosition + skipForwardAmount
        : playerPosition - skipBackwardAmount;
      let actualTargetPosition = desiredTargetPosition;

      if (
        (streamType === StreamType.StartOver ||
          streamType === StreamType.CatchUp ||
          streamType === StreamType.Vod) &&
        skipForward &&
        fastForwardLimitSeconds !== 0
      ) {
        if (desiredTargetPosition < fastForwardLimitSeconds) {
          actualTargetPosition = desiredTargetPosition;
        } else {
          actualTargetPosition = fastForwardLimitSeconds;
        }
      } else if (desiredTargetPosition >= seekableRange.end) {
        actualTargetPosition = seekableRange.end;
      } else if (desiredTargetPosition <= seekableRange.start) {
        actualTargetPosition = seekableRange.start;
      }

      setValidatedTargetPosition(actualTargetPosition);
      setPseudoPlayerPosition(actualTargetPosition);

      timeoutSkipIdRef.current = setTimeout(() => {
        setIsSkipButtonInProgress(false);
        setStartTimeFF(null);
        setStartTimeRW(null);
        player.setPosition(actualTargetPosition);
        setCurrentFFSeekSpeedMs(0);
        setCurrentRWSeekSpeedMs(0);
        player.play();
        setIsPlaying(true);
        setPseudoPlayerPosition(-1);
        setAutoHideTimeoutExtension(0);
      }, 1000);
    },
    [
      player,
      playerWaiting,
      trickmodes,
      showToast,
      setIsPlaying,
      setIsSkipping,
      setIsSkipButtonInProgress,
      setAutoHideTimeoutExtension,
      pseudoPlayerPosition,
      streamType,
      durationSeconds,
      startTimeFF,
      startTimeRW,
      currentFFSeekSpeedMs,
      currentRWSeekSpeedMs,
      fastForwardLimitSeconds,
      setValidatedTargetPosition,
      idealSeekSpeed
    ]
  );

  return {
    skip,
    setPlayerBtnsFocusHistory
  };
};

const SkipBackward = () => {
  const { skip, setPlayerBtnsFocusHistory } = useSkipButtons();
  const isDeactivated = useDeactivatePlayerActions();

  return (
    <IconButton
      size="xl"
      focusKey={PLAYER_FOCUS_KEY_SKIP_BACKWARD}
      onClick={() => !isDeactivated && skip(false)}
      data-testid={testIds.player.playerControls.skipBackward}
      onNavFocus={() => setPlayerBtnsFocusHistory(PLAYER_FOCUS_KEY_SKIP_BACKWARD)}
      icon={<SkipBackwardIcon />}
    >
      Skip tilbage
    </IconButton>
  );
};

const SkipForward = () => {
  const { skip, setPlayerBtnsFocusHistory } = useSkipButtons();
  const isDeactivated = useDeactivatePlayerActions();

  return (
    <IconButton
      size="xl"
      focusKey={PLAYER_FOCUS_KEY_SKIP_FORWARD}
      onClick={() => !isDeactivated && skip(true)}
      data-testid={testIds.player.playerControls.skipForward}
      onNavFocus={() => setPlayerBtnsFocusHistory(PLAYER_FOCUS_KEY_SKIP_FORWARD)}
      icon={<SkipForwardIcon />}
    >
      Skip frem
    </IconButton>
  );
};

export { SkipBackward, SkipForward, useSkipButtons };
